import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NgForm } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { combineLatest, map } from 'rxjs';

import { Ga4AnalyticsService } from '../../../core/analytics/ga4-analytics.service';
import { AuthStateService } from '../../../core/auth/auth-state.service';
import { TopicApiModel } from '../../topics/models/topic.model';
import { TopicsService } from '../../topics/services/topics.service';
import {
  CreateCollectionPayload,
  NumeronsService,
  UpdateCollectionPayload,
} from '../services/numerons.service';
import {
  NumeronActionModel,
  NumeronCollectionModel,
  NumeronItemModel,
} from '../models/numeron.model';

type CollectionPageMode = 'create' | 'edit';
type CreateCollectionTab = 'media' | 'source' | 'categories' | 'actions';

interface CreateCollectionActionFormModel {
  enabled: boolean;
  name: string;
  value: string;
}

interface CreateCollectionFormModel {
  title: string;
  type: string;
  visibility: boolean;
  collabMode: boolean;
  anonMode: boolean;
  assetPath: string;
  source: string;
  url: string;
  topicId: string;
  categories: string;
  relatedContentURL: string;
  action1: CreateCollectionActionFormModel;
  action2: CreateCollectionActionFormModel;
  action3: CreateCollectionActionFormModel;
  action4: CreateCollectionActionFormModel;
  action5: CreateCollectionActionFormModel;
}

@Component({
  selector: 'app-create-collection-page',
  imports: [FormsModule, RouterLink],
  templateUrl: './create-collection-page.component.html',
})
export class CreateCollectionPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly ga4AnalyticsService = inject(Ga4AnalyticsService);
  private readonly numeronsService = inject(NumeronsService);
  private readonly topicsService = inject(TopicsService);
  private readonly authStateService = inject(AuthStateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly activeTab = signal<CreateCollectionTab>('media');
  protected readonly isLoading = signal(true);
  protected readonly isSubmitting = signal(false);
  protected readonly pageMode = signal<CollectionPageMode>('create');
  protected readonly collectionId = signal('');
  protected readonly cancelHref = signal('/mynumerons');
  protected readonly topicLoadErrorMessage = signal('');
  protected readonly submitErrorMessage = signal('');
  protected readonly loadErrorMessage = signal('');
  protected readonly topicOptions = signal<TopicApiModel[]>([]);
  protected readonly selectedTopicLabel = signal('');
  protected readonly firstNumeronId = signal('');
  protected readonly firstNumeron = signal<NumeronItemModel | null>(null);

  protected readonly formModel: CreateCollectionFormModel = this.createDefaultFormModel();

  public constructor() {
    this.loadTopics();

    combineLatest([this.route.data, this.route.paramMap, this.route.queryParamMap])
      .pipe(
        map(([data, paramMap, queryParamMap]) => ({
          mode: (data['mode'] === 'edit' ? 'edit' : 'create') as CollectionPageMode,
          collectionId: paramMap.get('collectionId') ?? '',
          returnTo: queryParamMap.get('returnTo') ?? '',
        })),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ mode, collectionId, returnTo }) => {
        this.pageMode.set(mode);
        this.collectionId.set(collectionId);
        this.cancelHref.set(this.resolveCancelHref(mode, collectionId, returnTo));
        this.resetFormState();

        if (mode === 'edit') {
          this.title.setTitle('Edit Collection');
          this.ga4AnalyticsService.trackPageView({
            contentId: collectionId || undefined,
            contentType: 'Edit Collection',
            contentGroup: 'My Numerons',
          });
          this.loadCollection(collectionId);
          return;
        }

        this.title.setTitle('New Collection');
        this.ga4AnalyticsService.trackPageView({
          contentType: 'New Collection',
          contentGroup: 'My Numerons',
        });
        this.isLoading.set(false);
      });
  }

  protected setActiveTab(tab: CreateCollectionTab): void {
    this.activeTab.set(tab);
  }

  protected isActiveTab(tab: CreateCollectionTab): boolean {
    return this.activeTab() === tab;
  }

  protected isEditMode(): boolean {
    return this.pageMode() === 'edit';
  }

  protected pageHeading(): string {
    return this.isEditMode() ? 'Edit Collection' : 'New Collection';
  }

  protected submitButtonLabel(): string {
    if (this.isSubmitting()) {
      return this.isEditMode() ? 'Updating...' : 'Creating...';
    }

    return this.isEditMode() ? 'Update Collection' : 'Create Collection';
  }

  protected cannotUpdateEmptyCollection(): boolean {
    return this.isEditMode() && this.firstNumeronId().length === 0;
  }

  protected loadingMessage(): string {
    return this.isEditMode() ? 'Loading collection details...' : 'Preparing new collection form...';
  }

  protected hasActionValidationError(): boolean {
    return (
      this.isFixedActionInvalid(this.formModel.action1) ||
      this.isFixedActionInvalid(this.formModel.action2) ||
      this.isCustomActionInvalid(this.formModel.action3) ||
      this.isCustomActionInvalid(this.formModel.action4) ||
      this.isCustomActionInvalid(this.formModel.action5)
    );
  }

  protected showActionValueError(action: CreateCollectionActionFormModel): boolean {
    return action.enabled && action.value.trim().length === 0;
  }

  protected showCustomActionNameError(action: CreateCollectionActionFormModel): boolean {
    return action.enabled && action.name.trim().length === 0;
  }

  protected showCustomActionValueError(action: CreateCollectionActionFormModel): boolean {
    return action.enabled && action.value.trim().length === 0;
  }

  protected isMissingSelectedTopic(): boolean {
    const topicId = this.formModel.topicId.trim();
    return (
      topicId.length > 0 &&
      !this.topicOptions().some((topic) => topic._id === this.formModel.topicId)
    );
  }

  protected async onSubmit(form: NgForm): Promise<void> {
    this.submitErrorMessage.set('');

    if (this.cannotUpdateEmptyCollection()) {
      return;
    }

    if (form.invalid || this.hasActionValidationError()) {
      if (!this.formModel.topicId.trim()) {
        this.setActiveTab('categories');
      } else if (this.hasActionValidationError()) {
        this.setActiveTab('actions');
      }

      form.control.markAllAsTouched();
      return;
    }

    this.isSubmitting.set(true);

    const request$ = this.isEditMode()
      ? this.numeronsService.updateCollection(this.buildUpdatePayload())
      : this.numeronsService.createCollection(this.buildCreatePayload());

    request$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: async (collection) => {
        this.isSubmitting.set(false);
        await this.router.navigateByUrl(`/mynumerons/collection/${collection._id}`);
      },
      error: () => {
        this.isSubmitting.set(false);
        this.submitErrorMessage.set(
          this.isEditMode()
            ? 'Oops, could not update Collection!'
            : 'Oops, could not create Collection!',
        );
      },
    });
  }

  private loadTopics(): void {
    this.topicsService
      .getTopics()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (topics) => {
          this.topicOptions.set(topics);
          this.topicLoadErrorMessage.set('');
        },
        error: () => {
          this.topicOptions.set([]);
          this.topicLoadErrorMessage.set('Topics are unavailable right now. Please try again.');
        },
      });
  }

  private loadCollection(collectionId: string): void {
    this.isLoading.set(true);
    this.loadErrorMessage.set('');

    if (!collectionId) {
      this.loadErrorMessage.set('Collection details are unavailable right now. Please try again.');
      this.isLoading.set(false);
      return;
    }

    this.numeronsService
      .getMyNumeronCollection(collectionId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (collection) => {
          if (!collection.isCollection) {
            this.loadErrorMessage.set(
              'Collection details are unavailable right now. Please try again.',
            );
            this.isLoading.set(false);
            return;
          }

          if (!this.isPublisherMatch(collection)) {
            this.loadErrorMessage.set('You can edit only Collections published by your account.');
            this.isLoading.set(false);
            return;
          }

          this.populateForm(collection);
          this.title.setTitle(collection.title?.trim() || 'Edit Collection');
          this.isLoading.set(false);
        },
        error: () => {
          this.loadErrorMessage.set(
            'Collection details are unavailable right now. Please try again.',
          );
          this.isLoading.set(false);
        },
      });
  }

  private populateForm(collection: NumeronCollectionModel): void {
    const normalizedCategories = (collection.categories ?? []).join(', ');
    const actions = collection.actions ?? [];
    const fixedActionOne = actions.find((action) => action.identifier === 'CallNow');
    const fixedActionTwo = actions.find((action) => action.identifier === 'SendMessage');
    const customActions = actions.filter(
      (action) => action.identifier !== 'CallNow' && action.identifier !== 'SendMessage',
    );

    this.formModel.title = collection.title ?? '';
    this.formModel.type = collection.type?.trim() || 'Standard';
    this.formModel.visibility = this.asBoolean(collection.visibility);
    this.formModel.collabMode = this.isModeEnabled(collection.mode, 16);
    this.formModel.anonMode = this.isModeEnabled(collection.mode, 1);
    this.formModel.assetPath = collection.assetPath ?? '';
    this.formModel.source = collection.source ?? '';
    this.formModel.url = collection.url ?? '';
    this.formModel.topicId = collection.topic?._id ?? '';
    this.formModel.categories = normalizedCategories;
    this.formModel.relatedContentURL = collection.relatedContentURL ?? '';
    this.selectedTopicLabel.set(collection.topic?.topic ?? '');
    this.firstNumeronId.set(collection.numerons[0]?._id ?? '');
    this.firstNumeron.set(collection.numerons[0] ?? null);

    this.applyActionToForm(this.formModel.action1, fixedActionOne, 'Call Now');
    this.applyActionToForm(this.formModel.action2, fixedActionTwo, 'Send Message');
    this.applyActionToForm(this.formModel.action3, customActions[0]);
    this.applyActionToForm(this.formModel.action4, customActions[1]);
    this.applyActionToForm(this.formModel.action5, customActions[2]);
  }

  private buildCreatePayload(): CreateCollectionPayload {
    return {
      collectionTitle: this.formModel.title,
      collectionSource: this.formModel.source,
      collectionURL: this.formModel.url,
      collectionCategories: this.formModel.categories,
      relatedContentURL: this.formModel.relatedContentURL,
      collectionType: this.formModel.type,
      collectionAssetPath: this.formModel.assetPath,
      collectionVisibility: this.formModel.visibility,
      collabMode: this.formModel.collabMode,
      anonMode: this.formModel.anonMode,
      topicId: this.formModel.topicId,
      collectionActions: JSON.stringify(this.buildActions()),
    };
  }

  private buildUpdatePayload(): UpdateCollectionPayload {
    const firstNumeron = this.firstNumeron();

    return {
      collectionId: this.collectionId(),
      numeronId: this.firstNumeronId(),
      ...this.buildCreatePayload(),
      numeral: firstNumeron?.numeral ?? '',
      title: firstNumeron?.title ?? '',
      source: firstNumeron?.source ?? '',
      url: firstNumeron?.url ?? '',
      categories: this.joinCategories(firstNumeron?.categories),
      assetPath: firstNumeron?.assetPath ?? '',
      subType: firstNumeron?.subType ?? '',
      numeronActions: JSON.stringify(this.buildLegacyActionPayload(firstNumeron?.actions ?? [])),
    };
  }

  private buildActions(): {
    actionInternal: boolean;
    identifier: string;
    name: string;
    url: string;
  }[] {
    const actions: {
      actionInternal: boolean;
      identifier: string;
      name: string;
      url: string;
    }[] = [];

    if (this.formModel.action1.enabled) {
      actions.push({
        actionInternal: true,
        identifier: 'CallNow',
        name: 'Call Now',
        url: this.formModel.action1.value.trim(),
      });
    }

    if (this.formModel.action2.enabled) {
      actions.push({
        actionInternal: true,
        identifier: 'SendMessage',
        name: 'Send Message',
        url: this.formModel.action2.value.trim(),
      });
    }

    for (const action of [this.formModel.action3, this.formModel.action4, this.formModel.action5]) {
      if (!action.enabled) {
        continue;
      }

      actions.push({
        actionInternal: false,
        identifier: action.name.trim(),
        name: action.name.trim(),
        url: action.value.trim(),
      });
    }

    return actions;
  }

  private buildLegacyActionPayload(actions: NumeronActionModel[]): {
    actionInternal: boolean;
    identifier: string;
    name: string;
    url: string;
  }[] {
    return actions
      .filter((action) => Boolean(action.url?.trim()))
      .map((action) => ({
        actionInternal: Boolean(action.actionInternal),
        identifier: action.identifier?.trim() || action.name?.trim() || '',
        name: action.name?.trim() || action.identifier?.trim() || '',
        url: action.url?.trim() || '',
      }))
      .filter((action) => action.identifier.length > 0 && action.name.length > 0);
  }

  private joinCategories(categories?: string[]): string {
    return (categories ?? [])
      .map((category) => category.trim())
      .filter((category) => category.length > 0)
      .join(',');
  }

  private applyActionToForm(
    formAction: CreateCollectionActionFormModel,
    action?: NumeronActionModel,
    fallbackName = '',
  ): void {
    formAction.enabled = Boolean(action?.url?.trim());
    formAction.name = action?.name?.trim() || fallbackName;
    formAction.value = action?.url?.trim() || '';
  }

  private isPublisherMatch(collection: NumeronCollectionModel): boolean {
    const authenticatedPublisherId = this.authStateService.authUser()?.backendUserId?.trim() ?? '';
    const authenticatedDisplayName = this.authStateService.authUser()?.displayName?.trim() ?? '';
    const firstNumeron = collection.numerons[0];
    const collectionPublisherId = collection.user?._id?.trim() ?? '';
    const numeronPublisherId = firstNumeron?.user?._id?.trim() ?? '';
    const collectionPublisherName = collection.user?.displayName?.trim() ?? '';
    const numeronPublisherName = firstNumeron?.user?.displayName?.trim() ?? '';

    if (authenticatedPublisherId) {
      return (
        authenticatedPublisherId === collectionPublisherId ||
        authenticatedPublisherId === numeronPublisherId
      );
    }

    if (authenticatedDisplayName) {
      return (
        authenticatedDisplayName === collectionPublisherName ||
        authenticatedDisplayName === numeronPublisherName
      );
    }

    return true;
  }

  private isFixedActionInvalid(action: CreateCollectionActionFormModel): boolean {
    return action.enabled && action.value.trim().length === 0;
  }

  private isCustomActionInvalid(action: CreateCollectionActionFormModel): boolean {
    return action.enabled && (action.name.trim().length === 0 || action.value.trim().length === 0);
  }

  private createDefaultFormModel(): CreateCollectionFormModel {
    return {
      title: '',
      type: 'Standard',
      visibility: false,
      collabMode: false,
      anonMode: false,
      assetPath: '',
      source: '',
      url: '',
      topicId: '',
      categories: '',
      relatedContentURL: '',
      action1: {
        enabled: false,
        name: 'Call Now',
        value: '',
      },
      action2: {
        enabled: false,
        name: 'Send Message',
        value: '',
      },
      action3: {
        enabled: false,
        name: '',
        value: '',
      },
      action4: {
        enabled: false,
        name: '',
        value: '',
      },
      action5: {
        enabled: false,
        name: '',
        value: '',
      },
    };
  }

  private resetFormState(): void {
    this.activeTab.set('media');
    this.isSubmitting.set(false);
    this.isLoading.set(this.isEditMode());
    this.submitErrorMessage.set('');
    this.loadErrorMessage.set('');
    this.selectedTopicLabel.set('');
    this.firstNumeronId.set('');
    this.firstNumeron.set(null);
    Object.assign(this.formModel, this.createDefaultFormModel());
  }

  private resolveCancelHref(
    mode: CollectionPageMode,
    collectionId: string,
    returnTo: string,
  ): string {
    if (returnTo.startsWith('/mynumerons')) {
      return returnTo;
    }

    if (mode === 'edit' && collectionId) {
      return `/mynumerons/collection/${collectionId}`;
    }

    return '/mynumerons';
  }

  private asBoolean(value?: number | boolean): boolean {
    return value === true || value === 1;
  }

  private isModeEnabled(mode: number | undefined, bit: number): boolean {
    return (Number(mode ?? 0) & bit) === bit;
  }
}
